import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { handleFeatureNotIncluded } from "../../utils/notifyPlanUpgrade";
import { useAuth } from "../../auth/AuthContext";
import api from "../../services/api";
import { listWaitlist, cancelWaitlist } from "../../services/waitlistService";
import { ButtonBack } from "../../components/ui";
import WaitlistItem from "./components/WaitlistItem";
import styles from "./Waitlist.module.css";

/** Mantém apenas entradas exibíveis (o backend já filtra, mas garantimos no front). */
const VISIBLE_STATUSES = new Set(["ACTIVE", "NOTIFIED"]);

export default function WaitlistPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [removingId, setRemovingId] = useState(null);
  const [helpOpen, setHelpOpen] = useState(true);
  const [expiryDays, setExpiryDays] = useState(null);

  const tenantId = user?.tenantId;

  const fetchEntries = useCallback(async () => {
    if (!tenantId) {
      setStatus("error");
      return;
    }
    setStatus("loading");
    try {
      const res = await listWaitlist(tenantId);
      const data = Array.isArray(res.data) ? res.data : [];
      setEntries(data.filter((e) => VISIBLE_STATUSES.has(e.status)));
      setStatus("ready");
    } catch {
      setStatus("error");
      toast.error("Erro ao carregar a lista de espera.");
    }
  }, [tenantId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Busca o waitlistExpiryDays real do tenant para o banner informativo.
  useEffect(() => {
    let active = true;
    api
      .get("/tenants/me")
      .then((res) => {
        if (active) setExpiryDays(res.data?.tenant?.waitlistExpiryDays ?? null);
      })
      .catch(() => {
        /* banner usa fallback textual se indisponível */
      });
    return () => {
      active = false;
    };
  }, []);

  const handleRemove = useCallback(
    async (id, clientName) => {
      const name = clientName || "este cliente";
      const ok = window.confirm(
        `Remover ${name} da lista de espera? Ele não será mais notificado quando uma vaga abrir.`,
      );
      if (!ok) return;

      setRemovingId(id);
      try {
        await cancelWaitlist(id);
        setEntries((prev) => prev.filter((e) => e.id !== id));
        toast.success("Cliente removido da lista de espera.");
      } catch (err) {
        // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): mensagem
      // humana com CTA, nunca erro cru nem a copy genérica.
        if (!handleFeatureNotIncluded(err)) {
          toast.error("Erro ao remover o cliente da fila.");
        }
      } finally {
        setRemovingId(null);
      }
    },
    [],
  );

  const count = entries.length;
  const expiryText = expiryDays != null ? `${expiryDays} dias` : "alguns dias";

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <ButtonBack to="/agenda" />
          <div className={styles.headerText}>
            <h1 className={styles.title}>Lista de Espera</h1>
            <p className={styles.subtitle}>
              Clientes aguardando vaga quando todos os horários estavam ocupados.
            </p>
          </div>
          {status === "ready" && count > 0 && (
            <span className={styles.countBadge} role="status" aria-live="polite">
              {count} {count === 1 ? "pessoa aguardando" : "pessoas aguardando"}
            </span>
          )}
        </div>

        {/* Banner "Como funciona" — colapsável */}
        {helpOpen && (
          <div className={styles.helpBanner} role="note">
            <p className={styles.helpText}>
              <strong>ℹ️ Como funciona:</strong> quando um cliente tenta agendar e
              não há vagas, ele entra automaticamente nesta lista. Ao cancelar um
              agendamento, o próximo da fila recebe uma mensagem automática no
              WhatsApp. Entradas expiram após {expiryText}.
            </p>
            <button
              type="button"
              className={styles.helpClose}
              aria-label="Fechar aviso de funcionamento"
              onClick={() => setHelpOpen(false)}
            >
              ✕
            </button>
          </div>
        )}

        {status === "loading" && (
          <p className={styles.stateText} role="status" aria-live="polite">
            Carregando lista de espera...
          </p>
        )}

        {status === "error" && (
          <div className={styles.errorBox}>
            <p className={styles.stateText}>
              Não foi possível carregar a lista de espera.
            </p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={fetchEntries}
            >
              Tentar novamente
            </button>
          </div>
        )}

        {status === "ready" && count === 0 && (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon} aria-hidden="true">
              ⏳
            </span>
            <h2 className={styles.emptyTitle}>
              Nenhum cliente na lista de espera
            </h2>
            <p className={styles.emptyText}>
              Quando todos os horários estiverem ocupados e um cliente tentar
              agendar, ele será colocado automaticamente aqui. Assim que uma vaga
              abrir, ele é notificado pelo WhatsApp.
            </p>
          </div>
        )}

        {status === "ready" && count > 0 && (
          <ul className={styles.list}>
            {entries.map((entry) => (
              <WaitlistItem
                key={entry.id}
                entry={entry}
                onRemove={handleRemove}
                removing={removingId === entry.id}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
